import { createSelector } from 'reselect';
import createFilterOptions from 'react-select-fast-filter-options';
import { getQueryFromState } from  'utils/query';
import { getNotesQuestionnaire } from 'utils/authScopes';
import { compact, get, isBoolean, isEmpty, filter, find, some, reduce, split, isNull,
  flatMap, map, camelCase, findIndex, values, kebabCase, pick } from 'lodash';
import { API_FAILURE } from 'actions';

// Selector for page title
export const titleSelector = (state, key) => get(state, key, null);
export const getTitle = createSelector(titleSelector, title => title ? `Sparkler - ${title}` : 'Sparkler');

// Selector for players search
export const playersOptionsSelector = state => state.players || {};
export const getPlayersIndexedOptions = createSelector(playersOptionsSelector, options =>
  createFilterOptions({ options })
);

// Selector for accounts search
export const accountsOptionsSelector = state => state.accounts || {};
export const getAccountsIndexedOptions = createSelector(accountsOptionsSelector, options =>
  createFilterOptions({ options })
);

// Selector for teachers search
export const teachersOptionsSelector = state => state.teachers || {};
export const getTeachersIndexedOptions = createSelector(teachersOptionsSelector, options =>
  createFilterOptions({ options })
);

// Selector for loading indicator
export const loadersSelector = (state, filters) =>
  state.auth.isAuthChecked && !state.loaders[API_FAILURE] ? filters.map(f => state.loaders[f]) : [false];
export const getLoaders = createSelector(loadersSelector, loaders => some(loaders, Boolean));
export const getStrictLoaders = createSelector(loadersSelector, loaders => {
  return isEmpty(filter(loaders, l => isBoolean(l))) || some(loaders, Boolean);
});


// Selector for survey title
export const surveySelector = state => state.survey.survey || {};
export const getSurvey = createSelector(surveySelector, survey =>
  isEmpty(survey) ? {} : survey.entities.surveys[survey.result]
);

// Selector for cheking if required number of answers answered
const requiredAnswersSize = (entities, question) => {
  return reduce(question.responseTypes, (num, r) =>
    entities.responseTypes[r].required && !isNull(entities.responseTypes[r].answer) ? num + 1 : num, 0
  );
}

const checkSection = (entities, section) => {
  const requiredAnswersCount = section.requiredAnswersCount;
  const requiredAnsweredCount = reduce(section.questions, (num, q) => {
    return num + requiredAnswersSize(entities, entities.questions[q]);
  }, 0);
  return requiredAnsweredCount < requiredAnswersCount;
}

export const getIsSurveyValid = createSelector(surveySelector, survey =>
  isEmpty(survey) ? false : !some(survey.entities.sections, s => checkSection(survey.entities, s))
);

// Selector (not memoized bc we use it at componentDidMount) to get first uncompleted section
export const getSurveyStartSection = survey =>
  isEmpty(survey) ? 1 : findIndex(values(survey.entities.sections), s => checkSection(survey.entities, s)) + 1

// Selector for subgroups filters
const groupSubgroups = (subgroups) => flatMap(subgroups, (v, k) =>
  [{ value: camelCase(k), label: k, disabled: true }].concat(map(v, v => ({ value: v.value, label: v.name }))));
export const subgroupsSelector = state => state.filters.subgroups;
export const getSubgroups = createSelector(subgroupsSelector, subgroups => groupSubgroups(subgroups));
export const getSubgroupsWithGroups = createSelector(
  [ state => map(compact(split(state.meta.surveys.groups, ',')), gId => get(find(state.filters.groups, { 'value': parseInt(gId, 10) }), 'label')),
    state => state.filters.subgroups],
  (groupNames, subgroups) => {
    const filteredSubgroups = isEmpty(groupNames) ? subgroups : pick(subgroups, groupNames);
    return groupSubgroups(filteredSubgroups);
  }
);

// Selector for cohort filters
const groupCohorts = (cohorts) => flatMap(cohorts, (v, k) =>
  [{ value: camelCase(k), label: k, disabled: true }].concat(map(v, v => ({ value: v.value, label: v.name }))));
export const cohortsSelector = state => state.filters.cohorts;
export const getCohorts = createSelector(cohortsSelector, cohorts => groupCohorts(cohorts));
export const getCohortsWithGroups = createSelector(
  [ state => map(compact(split(state.meta.surveys.groups, ',')), gId => get(find(state.filters.groups, { 'value': parseInt(gId, 10) }), 'label')),
    state => state.filters.cohorts],
  (groupNames, cohorts) => {
    const filteredCohorts = isEmpty(groupNames) ? cohorts : pick(cohorts, groupNames);
    return groupCohorts(filteredCohorts);
  }
);

// Selector for ages filters
export const getColumnsAges = createSelector(state => state, ages => map(ages, v => { return { value: kebabCase(v.label), label: v.label } }));

// Selector for player webNotes size
export const getWebNotesCount = createSelector(state => state || {}, survey => {
  if (isEmpty(survey)) return 0;
  return reduce(survey.entities.surveys, (sum, survey) => sum + survey.notesCount, 0);
});

// Selector for dashboard query
export const getDashboardQuery = createSelector(state => state || {}, meta => getQueryFromState(meta));

// Selector for web notes for question
export const getWebNotes = createSelector(
  [(state, qId) => get(state, `entities.questions[${qId}]`),
    state => get(state, 'entities.notes')],
  (question, notes) =>  question ? question.notes.map( i => notes[i]) : []
);

// Selector for questionnaire for notes
const getNotesQuestionnairePath = createSelector(state => state, auth => getNotesQuestionnaire(auth))
export const getNotesQuestionnaireId = (state) => get(state.player.survey, getNotesQuestionnairePath(state.auth))

// Selector for any survey
export const getAnySurvey = createSelector(state => state.player.survey, survey => {
  return get(survey, `entities.surveys[${get(survey, 'result.questionnaires.parent')}]`, null);
});

// Selector for teacher survey
export const getTeachersSurveyObject = (survey) => get(survey, `entities.surveys[${get(survey, 'result.questionnaires.teacher')}]`, null)

export const getTeachersSurvey = createSelector(state => state.player.survey, survey => getTeachersSurveyObject(survey));

export const getTeachersScores = createSelector(getTeachersSurvey, survey =>
  survey ? survey.status === 'finished' && isNull(survey.asqId) && !isNull(survey.asqJid) : false
);

export const isTeacherGradingFailed = createSelector(getTeachersSurvey, survey =>
  survey ? survey.status === 'finished' && isNull(survey.asqId) && isNull(survey.asqJid) : false
);




export const selectAgeRangeOptions = (state) => {
  return state.filters.ages;
};

export const selectSkillsOptions = (state) => {
  return state.filters.skills;
};
